import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import api from '../services/api';
import './StockVO.css';

function StockVO({ user }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filePreview, setFilePreview] = useState(null); // { rows, colMap, filename }
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastImport, setLastImport] = useState(null); // { count, date }
  const [search, setSearch] = useState('');
  const [filterConcession, setFilterConcession] = useState('');
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);
  const [showImportChoice, setShowImportChoice] = useState(false);

  const isSuperAdmin = user?.role === 'super-admin' || user?.role === 'admin';

  const loadEntries = async () => {
    try {
      setLoading(true);
      const res = await api.get('/stock-vo');
      setEntries(res.data.entries);
    } catch (error) {
      console.error('Erreur chargement stock VO:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadEntries(); }, []);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ['immat', 'marque', 'concession'],
      ['AB-123-CD', 'Peugeot', 'CENTRAUTODIS'],
      ['EF-456-GH', 'Renault', 'BLUE'],
    ]);
    XLSX.utils.book_append_sheet(wb, ws, 'Modèle');
    XLSX.writeFile(wb, 'modele_stock_vo.xlsx');
  };

  // Détection auto des colonnes (case-insensitive, premier header qui contient le mot)
  const detectColumns = (headers) => {
    const h = headers.map(col => (col || '').toString().toLowerCase());
    const find = (...keywords) => {
      const idx = h.findIndex(col => keywords.some(k => col.includes(k)));
      return idx >= 0 ? headers[idx] : null;
    };
    return {
      immat: find('immat', 'plaque', 'immatriculation'),
      marque: find('marque', 'brand'),
      concession: find('concession', 'site'),
    };
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const jsonRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        if (jsonRows.length < 2) {
          showToast('Fichier vide ou sans données', 'error');
          return;
        }

        const headers = jsonRows[0].map(h => (h || '').toString());
        const colMap = detectColumns(headers);

        if (!colMap.immat) {
          showToast('Colonne immat/plaque/immatriculation introuvable', 'error');
          return;
        }

        const rows = jsonRows.slice(1).map(row => {
          const get = (key) => {
            if (!colMap[key]) return '';
            const idx = headers.indexOf(colMap[key]);
            return idx >= 0 ? (row[idx] || '').toString().trim() : '';
          };
          return {
            immat: get('immat'),
            marque: get('marque'),
            concession: get('concession'),
          };
        }).filter(r => r.immat);

        setFilePreview({ rows, colMap, filename: file.name });
      } catch (err) {
        showToast('Erreur de lecture du fichier', 'error');
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleSyncSftp = async () => {
    if (syncing) return;
    if (!window.confirm('Synchroniser Stock VO depuis le SFTP (stockvo.all.csv) ?\n\nLe contenu actuel sera entièrement remplacé.')) return;
    try {
      setSyncing(true);
      const res = await api.post('/stock-vo/sync');
      const { imported, total_rows, detected_columns } = res.data;
      setLastImport({ count: imported, date: new Date().toLocaleString('fr-FR') });
      await loadEntries();
      const cols = detected_columns ? ` · colonnes détectées : immat=${detected_columns.immat || '—'}, marque=${detected_columns.marque || '—'}, concession=${detected_columns.concession || '—'}` : '';
      showToast(`${imported} véhicule${imported !== 1 ? 's' : ''} synchronisé${imported !== 1 ? 's' : ''} (${total_rows} ligne${total_rows !== 1 ? 's' : ''} dans le CSV)${cols}`);
    } catch (error) {
      showToast(error.response?.data?.error || 'Erreur lors de la synchronisation SFTP', 'error');
    } finally {
      setSyncing(false);
    }
  };

  const confirmImport = async () => {
    if (!filePreview) return;
    try {
      setImporting(true);
      const res = await api.post('/stock-vo/import', { rows: filePreview.rows });
      const count = res.data.imported;
      setLastImport({ count, date: new Date().toLocaleString('fr-FR') });
      setFilePreview(null);
      await loadEntries();
      showToast(`${count} véhicule${count !== 1 ? 's' : ''} importé${count !== 1 ? 's' : ''} avec succès`);
    } catch (error) {
      showToast(error.response?.data?.error || 'Erreur lors de l\'import', 'error');
    } finally {
      setImporting(false);
    }
  };

  const concessions = [...new Set(entries.map(e => e.concession).filter(Boolean))].sort();

  const filteredEntries = entries.filter(e => {
    if (filterConcession && e.concession !== filterConcession) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !e.immat?.toLowerCase().includes(q) &&
        !e.marque?.toLowerCase().includes(q) &&
        !e.concession?.toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  const formatDate = (s) => {
    if (!s) return '-';
    try { return new Date(s).toLocaleDateString('fr-FR'); } catch { return s; }
  };

  return (
    <div className="page-container">
      {/* Onglet unique */}
      <div className="page-tabs">
        <button className="active">📦 Stock VO</button>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`stock-vo-toast ${toast.type}`}>{toast.msg}</div>
      )}

      {/* Toolbar */}
      <div className="page-toolbar stock-vo-toolbar">
        {isSuperAdmin && (
          <button
            className="btn-import-vo"
            onClick={handleSyncSftp}
            disabled={syncing}
            title="Télécharge stockvo.all.csv depuis le SFTP et remplace le stock"
            style={{ background: 'var(--color-accent)' }}
          >
            {syncing ? '⏳ Synchronisation…' : '🔁 Synchroniser SFTP'}
          </button>
        )}
        <button className="btn-import-vo" onClick={() => setShowImportChoice(true)}>
          📥 Importer un fichier Excel
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <div className="stock-vo-filters">
          <input
            type="text"
            placeholder="Rechercher (immat, marque, concession)..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="filter-input-vo"
          />
          <select
            value={filterConcession}
            onChange={e => setFilterConcession(e.target.value)}
            className="filter-input-vo"
          >
            <option value="">Toutes les concessions</option>
            {concessions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {(search || filterConcession) && (
            <button className="btn-clear-vo" onClick={() => { setSearch(''); setFilterConcession(''); }}>
              Effacer
            </button>
          )}
        </div>
        <span className="stock-vo-count">
          {filteredEntries.length} / {entries.length} véhicule{entries.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Infos dernier import / dernière sync */}
      {lastImport ? (
        <div className="stock-vo-last-import">
          Dernier import : {lastImport.count} véhicule{lastImport.count !== 1 ? 's' : ''} — {lastImport.date}
        </div>
      ) : entries.length > 0 && entries[0].synced_at ? (
        <div className="stock-vo-last-import">
          Dernière sync SFTP : {new Date(entries.reduce((max, e) => (e.synced_at && (!max || e.synced_at > max) ? e.synced_at : max), null)).toLocaleString('fr-FR')}
        </div>
      ) : null}

      {/* Aperçu fichier avant import */}
      {filePreview && (
        <div className="stock-vo-preview">
          <div className="preview-header">
            <strong>📄 {filePreview.filename}</strong>
            <span className="preview-count">
              {filePreview.rows.length} véhicule{filePreview.rows.length !== 1 ? 's' : ''} détecté{filePreview.rows.length !== 1 ? 's' : ''}
            </span>
            <span className="preview-cols">
              Colonnes : {Object.entries(filePreview.colMap).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join(', ')}
            </span>
          </div>
          <div className="preview-actions">
            <button className="btn-secondary" onClick={() => setFilePreview(null)}>Annuler</button>
            <button className="btn-primary" onClick={confirmImport} disabled={importing}>
              {importing ? 'Import en cours...' : `✅ Confirmer l'import (${filePreview.rows.length} lignes)`}
            </button>
          </div>
          <div className="preview-table-wrapper">
            <table className="preview-table">
              <thead>
                <tr>
                  <th>Immat</th>
                  <th>Marque</th>
                  <th>Concession</th>
                </tr>
              </thead>
              <tbody>
                {filePreview.rows.slice(0, 10).map((r, i) => (
                  <tr key={i}>
                    <td>{r.immat}</td>
                    <td>{r.marque || '-'}</td>
                    <td>{r.concession || '-'}</td>
                  </tr>
                ))}
                {filePreview.rows.length > 10 && (
                  <tr>
                    <td colSpan="3" className="preview-more">
                      … et {filePreview.rows.length - 10} ligne{filePreview.rows.length - 10 !== 1 ? 's' : ''} supplémentaire{filePreview.rows.length - 10 !== 1 ? 's' : ''}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tableau principal */}
      {loading ? (
        <div className="stock-vo-loading">Chargement...</div>
      ) : (
        <div className="table-container">
          <table className="stock-vo-table">
            <thead>
              <tr>
                <th>Immat</th>
                <th>Marque</th>
                <th>Concession</th>
                <th>Date MAJ</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.length === 0 ? (
                <tr>
                  <td colSpan="4" className="stock-vo-empty">
                    {entries.length === 0
                      ? 'Aucun véhicule en stock. Importez un fichier Excel pour commencer.'
                      : 'Aucun résultat pour cette recherche.'}
                  </td>
                </tr>
              ) : filteredEntries.map(entry => (
                <tr key={entry.id}>
                  <td><span className="plaque-badge">{entry.immat}</span></td>
                  <td>{entry.marque || '-'}</td>
                  <td>{entry.concession || '-'}</td>
                  <td>{formatDate(entry.date_maj)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* ── Modal choix import ── */}
      {showImportChoice && (
        <div className="import-choice-overlay" onClick={() => setShowImportChoice(false)}>
          <div className="import-choice-modal" onClick={e => e.stopPropagation()}>
            <div className="import-choice-header">
              <h3>📥 Importer un fichier Excel</h3>
              <button className="import-choice-close" onClick={() => setShowImportChoice(false)}>×</button>
            </div>
            <div className="import-choice-body">
              <button className="import-choice-btn btn-download" onClick={downloadTemplate}>
                <span className="choice-icon">📄</span>
                <span>Télécharger<br />le fichier modèle</span>
              </button>
              <button
                className="import-choice-btn btn-upload"
                onClick={() => { setShowImportChoice(false); fileInputRef.current?.click(); }}
              >
                <span className="choice-icon">📂</span>
                <span>Charger<br />le fichier</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StockVO;
